import {
  useReactFlow,
  useStoreApi,
  type FitViewOptions,
  type XYPosition,
} from '@xyflow/react';
import { useCallback } from 'react';

import { ReadableViewportHelper } from '../graph/readable-viewport-helper';

interface UseReadableFitViewParams {
  nodes: ReadonlyArray<{ position: XYPosition }>;
  nodeSize: { width: number; height: number };
  padding?: FitViewOptions['padding'];
}

// Must be called inside <ReactFlowProvider>. Replacement for rf.fitView that
// never shrinks the chart below ReadableViewportHelper.MIN_ZOOM.
function useReadableFitView({
  nodes,
  nodeSize,
  padding,
}: Readonly<UseReadableFitViewParams>) {
  const rf = useReactFlow();
  const store = useStoreApi();
  const { width: nodeWidth, height: nodeHeight } = nodeSize;

  return useCallback(
    (options?: { duration?: number }) => {
      const { width, height } = store.getState();
      const viewport = ReadableViewportHelper.getViewport({
        nodePositions: nodes.map((n) => n.position),
        nodeSize: { width: nodeWidth, height: nodeHeight },
        canvasSize: { width, height },
        padding,
      });
      if (viewport) rf.setViewport(viewport, options);
    },
    [rf, store, nodes, nodeWidth, nodeHeight, padding]
  );
}

export { useReadableFitView };
